import React, { useState, useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { getFileTypes } from 'ducks/files/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import Input from 'components/atoms/Input'
import Checkbox from 'components/atoms/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import useDebounce from 'utils/hooks/useDebounce'
import { isNotNilOrEmpty } from 'utils/ramda'
import { pathOr } from 'ramda'

const FileTypeMultiSelect = ({
  selectedFileTypes = [],
  setSelectedFileTypes,
  placeholder = 'Search and select file types...',
  files = [],
  autoOpen = false
}) => {
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(autoOpen)
  const [showSelected, setShowSelected] = useState(false)
  const resultsRef = useRef(null)
  const inputRef = useRef(null)
  const debouncedValue = useDebounce(searchQuery, 300)

  // Create category map for easier lookup
  const categoryMap = useMemo(() => {
    if (!fileCategories) return {}
    return fileCategories.reduce((acc, cat) => {
      acc[cat.id] = cat.name
      return acc
    }, {})
  }, [fileCategories])

  // Count uncategorised files
  const uncategorisedCount = useMemo(() => {
    return files.filter(file => !file.fileType?.id).length
  }, [files])

  // Create file type options with category info
  const fileTypeOptions = useMemo(() => {
    if (!fileTypes) return []
    
    const regularTypes = fileTypes.map(type => ({
      id: type.id,
      name: type.name,
      description: type.description,
      categoryId: type.fileCategoryId,
      categoryName: categoryMap[type.fileCategoryId] || 'Uncategorised'
    }))
    
    // Add special option for uncategorised files (null file_type_id) with count
    const uncategorisedOption = {
      id: 'null',
      name: uncategorisedCount > 0 ? `Uncategorised Files (${uncategorisedCount})` : 'Uncategorised Files',
      description: 'Files that have not been assigned a file type',
      categoryId: null,
      categoryName: 'Uncategorised'
    }
    
    return [uncategorisedOption, ...regularTypes]
  }, [fileTypes, categoryMap, uncategorisedCount])

  // Filter types based on search query
  const filteredTypes = useMemo(() => {
    if (!debouncedValue) return fileTypeOptions
    
    const query = debouncedValue.toLowerCase().trim()
    return fileTypeOptions.filter(type => {
      const searchableText = [
        type.name,
        type.description,
        type.categoryName
      ].filter(Boolean).join(' ').toLowerCase()
      
      return searchableText.includes(query)
    })
  }, [fileTypeOptions, debouncedValue])

  // Group filtered types by category for better display
  const groupedTypes = useMemo(() => {
    const groups = {}
    filteredTypes.forEach(type => {
      if (!groups[type.categoryName]) {
        groups[type.categoryName] = []
      }
      groups[type.categoryName].push(type)
    })
    
    // Sort categories and types within each category (Uncategorised first)
    const sortedGroups = {}
    const sortedCategoryNames = Object.keys(groups).sort((a, b) => {
      // Put "Uncategorised" first
      if (a === 'Uncategorised') return -1
      if (b === 'Uncategorised') return 1
      return a.localeCompare(b)
    })
    
    sortedCategoryNames.forEach(categoryName => {
      sortedGroups[categoryName] = groups[categoryName].sort((a, b) => a.name.localeCompare(b.name))
    })
    
    return sortedGroups
  }, [filteredTypes])

  // Get selected type names for display
  const selectedTypeNames = useMemo(() => {
    if (!selectedFileTypes.length || !fileTypes) return []
    return selectedFileTypes
      .map(id => fileTypes.find(type => type.id === id)?.name)
      .filter(Boolean)
  }, [selectedFileTypes, fileTypes])

  const handleTypeToggle = (typeId) => {
    const newSelected = selectedFileTypes.includes(typeId)
      ? selectedFileTypes.filter(id => id !== typeId)
      : [...selectedFileTypes, typeId]
    setSelectedFileTypes(newSelected)
  }

  const handleClearAll = () => {
    setSelectedFileTypes([])
  }

  const handleRemoveType = (typeId) => {
    setSelectedFileTypes(selectedFileTypes.filter(id => id !== typeId))
  }

  const handleSearch = (_, query) => {
    setSearchQuery(query)
  }

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      // For Material-UI TextField wrapped in Input component
      setTimeout(() => {
        const input = inputRef.current.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 100)
    }
  }, [open])

  const displayText = selectedFileTypes.length > 0 
    ? `${selectedFileTypes.length} file type${selectedFileTypes.length > 1 ? 's' : ''} selected`
    : placeholder

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <InputWrapper onClick={() => setOpen(true)}>
          <DisplayInput hasValue={selectedFileTypes.length > 0}>
            <DisplayText hasValue={selectedFileTypes.length > 0}>
              {displayText}
            </DisplayText>
            {selectedFileTypes.length > 0 && (
              <ActionButtons>
                <ActionButton onClick={(e) => { e.stopPropagation(); setShowSelected(!showSelected); }}>
                  {showSelected ? 'Hide' : 'Show'} Selected
                </ActionButton>
                <ActionButton onClick={(e) => { e.stopPropagation(); handleClearAll(); }}>
                  Clear All
                </ActionButton>
              </ActionButtons>
            )}
          </DisplayInput>
          <DropdownIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </DropdownIcon>
        </InputWrapper>

        {/* Selected types display */}
        {selectedFileTypes.length > 0 && showSelected && (
          <SelectedWrapper>
            {selectedTypeNames.map((typeName, index) => (
              <StyledChip
                key={selectedFileTypes[index]}
                label={typeName}
                onDelete={() => handleRemoveType(selectedFileTypes[index])}
                size="small"
              />
            ))}
          </SelectedWrapper>
        )}

        {open && (
          <Dropdown ref={resultsRef}>
            <SearchWrapper>
              <Input
                ref={inputRef}
                onChange={handleSearch}
                value={searchQuery}
                name='searchQuery'
                placeholder='Search file types...'
                nomarginbottom
              />
            </SearchWrapper>
            
            <ResultsWrapper>
              {Object.keys(groupedTypes).length === 0 ? (
                <NoResults>No file types found</NoResults>
              ) : (
                Object.entries(groupedTypes).map(([categoryName, types]) => (
                  <CategoryGroup key={categoryName}>
                    <CategoryHeader>{categoryName}</CategoryHeader>
                    {types.map(type => (
                      <TypeItem key={type.id}>
                        {type.description ? (
                          <Tooltip title={type.description} placement="right">
                            <TooltipWrapper>
                              <Checkbox
                                label={type.name}
                                name={type.id}
                                value={selectedFileTypes.includes(type.id)}
                                onChange={() => handleTypeToggle(type.id)}
                                noMargin
                              />
                            </TooltipWrapper>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            label={type.name}
                            name={type.id}
                            value={selectedFileTypes.includes(type.id)}
                            onChange={() => handleTypeToggle(type.id)}
                            noMargin
                          />
                        )}
                      </TypeItem>
                    ))}
                  </CategoryGroup>
                ))
              )}
            </ResultsWrapper>
            
            {selectedFileTypes.length > 0 && (
              <DropdownFooter>
                <FooterText>{selectedFileTypes.length} selected</FooterText>
                <FooterButton onClick={handleClearAll}>Clear All</FooterButton>
              </DropdownFooter>
            )}
          </Dropdown>
        )}
      </Container>
    </ClickAwayListener>
  )
}

export default FileTypeMultiSelect

const Container = styled.div`
  position: relative;
  width: 100%;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const InputWrapper = styled.div`
  position: relative;
  cursor: pointer;
`

const DisplayInput = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: 6px;
  padding: 10px 35px 10px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`

const DisplayText = styled.span`
  color: ${({ theme, hasValue }) => hasValue ? theme.colors.grey[700] : theme.colors.grey[500]};
  font-weight: 400;
`

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
  display: flex;
  align-items: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`

const SelectedWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 100px;
  overflow-y: auto;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.primary}05;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.primary}10;
`

const StyledChip = styled(Chip)`
  && {
    height: 24px;
    font-size: 11px;
    
    .MuiChip-deleteIcon {
      font-size: 14px;
    }
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  margin-top: 4px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SearchWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
`

const ResultsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey[100]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey[400]};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.grey[500]};
    }
  }
`

const CategoryGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: 2px;
  }
`

const CategoryHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[50]};
  padding: 10px 16px;
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[600]};
  letter-spacing: 0.5px;
`

const TypeItem = styled.div`
  padding: 10px 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[50]};
    padding-left: 20px;
  }
`

const TypeDescription = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: 4px;
  margin-left: 24px;
  line-height: 1.3;
`

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 14px;
`

const DropdownFooter = styled.div`
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FooterText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const FooterButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`

const TooltipWrapper = styled.div`
  width: 100%;
  cursor: pointer;
`